import { useMemo } from 'react';
import moment from 'moment';
import { OrderData, OrderPageData, OrderStatus, PassOut, PassStatus, TimeAvailabilityDisabledReason } from '../../api';
import { useAvailableDates } from '../../orderPage/multipleOrderPages/useAvailableDates';
import { useManagePasses } from '../ManagePassesContext';
import {
  getExperienceName,
  getPassOrderPage,
  getRedeemableOrderPageIds,
  hasPassStartedBy,
  isPassActiveForDate,
  isPassExpired,
} from '../utils/passUtils';
import { useLanguage } from '../../utils/GeneralUtils';

export enum ScheduleSessionStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  WAITLIST = 'waitlist',
  WAITLIST_REGISTERED = 'waitlist_registered',
  REGISTERED = 'registered',
  NOT_YET_ACTIVE = 'not_yet_active',
  PASS_EXPIRED = 'pass_expired',
}

export type ScheduleSession = {
  date: string;
  timeSeconds: number;
  eventId?: string;
  orderPageId: string;
  experienceId: string;
  workshopName: string;
  employeeName?: string;
  duration: number;
  ticketsLeft?: number;
  totalTickets?: number;
  status: ScheduleSessionStatus;
  registeredOrder?: OrderData;
  waitlistItemId?: string;
  passStartsOn?: string;
};

const passCoversOrderPage = (
  passOrderPage: { redeemable_on?: Array<{ order_page_id: string }> } | undefined,
  orderPageId: string,
): boolean => {
  if (!passOrderPage) return false;
  if (!passOrderPage.redeemable_on || passOrderPage.redeemable_on.length === 0) return true;
  return passOrderPage.redeemable_on.some((r) => r.order_page_id === orderPageId);
};

const getEarliestUpcomingStartDate = (
  passes: PassOut[],
  account: { pass_order_pages?: Array<{ id: string; redeemable_on?: Array<{ order_page_id: string }> }> } | undefined,
  orderPageId: string,
  sessionDate: string,
): string | undefined => {
  const eligible = passes.filter((pass) => {
    if (pass.status !== PassStatus.ACTIVE) return false;
    const passOP = getPassOrderPage(account as any, pass.order_page_id);
    return passCoversOrderPage(passOP, orderPageId) && !isPassExpired(pass, passOP);
  });
  if (eligible.some((pass) => hasPassStartedBy(pass, sessionDate))) {
    return undefined;
  }
  let earliest: string | undefined;
  for (const pass of eligible) {
    if (pass.start_date && (!earliest || moment(pass.start_date).isBefore(earliest))) {
      earliest = pass.start_date;
    }
  }
  return earliest;
};

const somePassCoversSessionDate = (
  passes: PassOut[],
  account: { pass_order_pages?: Array<{ id: string; redeemable_on?: Array<{ order_page_id: string }> }> } | undefined,
  orderPageId: string,
  sessionDate: string,
): boolean =>
  passes.some((pass) => {
    const passOP = getPassOrderPage(account as any, pass.order_page_id);
    return passCoversOrderPage(passOP, orderPageId) && isPassActiveForDate(pass, passOP, sessionDate);
  });

const getOrderPageData = (
  account: { experiences: Array<{ id: string; order_pages: OrderPageData[] }> },
  orderPageId: string,
): { orderPage?: OrderPageData; experienceId?: string } => {
  for (const exp of account.experiences || []) {
    const orderPage = exp.order_pages?.find((op) => op.id === orderPageId);
    if (orderPage) return { orderPage, experienceId: exp.id };
  }
  return {};
};

export const usePassScheduleData = () => {
  const { passes, orders, account, employeeIdToName, waitlistItems } = useManagePasses();
  const language = useLanguage();
  const passList: PassOut[] = passes;

  const redeemableOrderPageIds = useMemo(
    () => (account ? getRedeemableOrderPageIds(passes, account) : []),
    [passes, account],
  );

  const { availableDates, isLoading } = useAvailableDates(account?.id || '', redeemableOrderPageIds, 1);

  const scheduleData = useMemo(() => {
    if (!account || availableDates.length === 0) return [];

    const activeOrders = orders.filter(
      (o) =>
        o.date &&
        moment(o.date).isSameOrAfter(moment(), 'day') &&
        o.status !== OrderStatus.ARCHIVED &&
        !o.member_order_id,
    );

    const sessions: ScheduleSession[] = [];

    for (const opAvailability of availableDates) {
      const { orderPage, experienceId } = getOrderPageData(account, opAvailability.order_page_id);
      if (orderPage && experienceId) {
        const workshopName = getExperienceName(account, opAvailability.order_page_id, language);
        const hasWaitingList = orderPage.waiting_list?.enabled ?? false;

        for (const dayAvailability of opAvailability.availability) {
          for (const slot of dayAvailability.times) {
            const registeredOrder = activeOrders.find((o) => slot.eventId && o.parent_id === slot.eventId);

            const employeeName = slot.employee_ids?.[0] ? employeeIdToName[slot.employee_ids[0]] : undefined;

            const isSoldOut =
              slot.isDisabled &&
              (slot.timeDisableReason === TimeAvailabilityDisabledReason.NO_TICKETS_LEFT ||
                slot.timeDisableReason === TimeAvailabilityDisabledReason.MORE_PERSONS_THEN_TICKETS);

            const matchingWaitlistItem = waitlistItems.find(
              (item) =>
                (slot.eventId && item.event_id === slot.eventId) ||
                (!slot.eventId &&
                  item.date === dayAvailability.date &&
                  item.order_page_id === opAvailability.order_page_id),
            );

            let status: ScheduleSessionStatus;
            let waitlistItemId: string | undefined;
            let passStartsOn: string | undefined;
            const isJoinableWaitlist = isSoldOut && hasWaitingList;
            if (registeredOrder) {
              status = ScheduleSessionStatus.REGISTERED;
            } else if (isJoinableWaitlist && matchingWaitlistItem) {
              status = ScheduleSessionStatus.WAITLIST_REGISTERED;
              waitlistItemId = matchingWaitlistItem.id;
            } else if (slot.isDisabled && !isJoinableWaitlist) {
              status = ScheduleSessionStatus.CLOSED;
            } else {
              const earliestStart = getEarliestUpcomingStartDate(
                passList,
                account,
                opAvailability.order_page_id,
                dayAvailability.date,
              );
              if (earliestStart) {
                status = ScheduleSessionStatus.NOT_YET_ACTIVE;
                passStartsOn = earliestStart;
              } else if (
                !somePassCoversSessionDate(passList, account, opAvailability.order_page_id, dayAvailability.date)
              ) {
                status = ScheduleSessionStatus.PASS_EXPIRED;
              } else if (isJoinableWaitlist) {
                status = ScheduleSessionStatus.WAITLIST;
              } else {
                status = ScheduleSessionStatus.OPEN;
              }
            }

            sessions.push({
              date: dayAvailability.date,
              timeSeconds: slot.seconds,
              eventId: slot.eventId,
              orderPageId: opAvailability.order_page_id,
              experienceId,
              workshopName,
              employeeName,
              duration: orderPage.duration,
              ticketsLeft: slot.ticketsLeft,
              totalTickets: slot.totalTickets,
              status,
              registeredOrder,
              waitlistItemId,
              passStartsOn,
            });
          }
        }
      }
    }

    sessions.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.timeSeconds - b.timeSeconds;
    });

    return sessions;
  }, [account, availableDates, orders, employeeIdToName, language, waitlistItems, passList]);

  return { scheduleData, isLoading };
};
